/* ============================================================================
   Claim wizard — the six-step customer journey.
   ----------------------------------------------------------------------------
   The claim form was one long page: order lookup, item choice, reason,
   description and file upload all visible at once, with a single Submit at the
   bottom. Everything worked, but it asked someone who has just lost a parcel to
   hold the whole task in their head, and it gave no signal about how much was
   left.

   This splits the same journey into the six steps it always had:

     1 Verify order   order number + email, checked against this merchant
     2 Select items   the real order lines, with affected quantity
     3 What happened  reason + description
     4 Evidence       photos and documents, queued before submit
     5 Review         everything, with a way back to each step
     6 Submitted      claim reference and the private tracking link

   What is deliberately NOT changed:

   * The API calls and their order. `verify` still POSTs verify-order, `submit`
     still POSTs the claim and only then drains the evidence queue, so a failed
     upload cannot lose a claim that was already accepted.
   * Server authority. The wizard never decides eligibility, never prices
     anything, and never trusts its own copy of the order — every step's data
     came from `verifyPublicOrder`.
   * Quantity rules. Still 1..ordered, still validated again at submit, because
     the client check is a courtesy and the server's is the real one.

   Progress is a real `<ol>` with `aria-current`, not a decorative bar: someone
   using a screen reader gets the same "step 3 of 6" the sighted user gets.
   ========================================================================== */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { claimMotion, claimStepVariants } from './motion';
import { focusClaimHeading } from './focus';

export const CLAIM_STEPS = [
  { id: 'verify', label: 'Verify order' },
  { id: 'items', label: 'Select items' },
  { id: 'details', label: 'What happened' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'review', label: 'Review' },
  { id: 'done', label: 'Submitted' },
] as const;

export type ClaimStepId = (typeof CLAIM_STEPS)[number]['id'];


export function useClaimWizard() {
  const [index, setIndex] = useState(0);
  /** Highest step reached, so completed steps stay clickable after going back. */
  const [furthest, setFurthest] = useState(0);

  const goTo = useCallback((next: number) => {
    setIndex(next);
    setFurthest((current) => Math.max(current, next));
  }, []);

  const next = useCallback(() => goTo(Math.min(index + 1, CLAIM_STEPS.length - 1)), [goTo, index]);
  const back = useCallback(() => setIndex((current) => Math.max(current - 1, 0)), []);

  return {
    index,
    furthest,
    step: CLAIM_STEPS[index].id,
    goTo,
    next,
    back,
    isDone: CLAIM_STEPS[index].id === 'done',
  };
}

export function ClaimProgress({
  index,
  furthest,
  onSelect,
}: {
  index: number;
  furthest: number;
  onSelect: (next: number) => void;
}) {
  const reduced = useReducedMotion() ?? false;
  // The final step is not shown as a target: it is the outcome, not a stage
  // someone navigates to.
  const shown = CLAIM_STEPS.slice(0, CLAIM_STEPS.length - 1);
  const percent = Math.round((Math.min(index, shown.length - 1) / (shown.length - 1)) * 100);

  return (
    <nav className="cw-progress" aria-label="Claim progress">
      <p className="cw-progress__count">
        Step {Math.min(index + 1, shown.length)} of {shown.length}
        <span className="cw-progress__label"> · {CLAIM_STEPS[index].label}</span>
      </p>

      <div
        className="cw-progress__track"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Claim completion"
      >
        <motion.span
          className="cw-progress__fill"
          data-motion="claim-progress"
          data-reduced-motion={reduced ? 'true' : 'false'}
          initial={false}
          animate={{ scaleX: percent / 100 }}
          transition={{ duration: reduced ? 0 : claimMotion.duration.interface, ease: claimMotion.ease.standard }}
        />
      </div>

      <ol className="cw-progress__steps">
        {shown.map((step, stepIndex) => {
          const state = stepIndex < index ? 'done' : stepIndex === index ? 'current' : 'upcoming';
          // Only steps already reached are reachable — jumping ahead would skip
          // the verification the later steps depend on.
          const reachable = stepIndex <= furthest && stepIndex !== index;
          return (
            <li key={step.id} data-state={state}>
              {reachable ? (
                <button type="button" onClick={() => onSelect(stepIndex)}>
                  <span className="cw-progress__dot" aria-hidden="true">{stepIndex + 1}</span>
                  <span>{step.label}</span>
                </button>
              ) : (
                <span aria-current={state === 'current' ? 'step' : undefined}>
                  <span className="cw-progress__dot" aria-hidden="true">{stepIndex + 1}</span>
                  <span>{step.label}</span>
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * Animated step container. The direction is passed in so moving forward and
 * moving back read differently — the motion says which way through the task
 * you are going, which is the only reason it is here.
 */
export function ClaimStepPanel({
  stepId,
  direction,
  title,
  hint,
  onSubmit,
  children,
}: {
  stepId: ClaimStepId;
  direction: 1 | -1;
  title: string;
  hint?: ReactNode;
  /**
   * What Enter in a text field should do on this step.
   *
   * The wizard had no `<form>` anywhere, so implicit submission — the oldest
   * habit a person has with a text field — did nothing at all. A shopper typed
   * an order number, pressed Enter, and the page sat still with no error and no
   * movement; the only way forward was to notice the button.
   *
   * Given only on steps whose primary action is advancing from typed input.
   * A step whose action is a choice or an upload has nothing for Enter to
   * submit, and wrapping it in a form would swallow the key instead.
   */
  onSubmit?: () => void;
  children: ReactNode;
}) {
  const reduced = useReducedMotion() ?? false;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const variants = claimStepVariants(direction, reduced);

  useEffect(() => {
    focusClaimHeading(headingRef.current, stepId !== 'verify');
  }, [stepId]);

  return (
    <motion.section
      className="cw-step"
      data-testid="claim-step-panel"
      data-motion="claim-step"
      data-motion-direction={direction}
      data-reduced-motion={reduced ? 'true' : 'false'}
      aria-labelledby={`cw-step-${stepId}`}
      initial={variants.enter}
      animate={variants.center}
      onAnimationComplete={() => {
        if (document.activeElement === headingRef.current) focusClaimHeading(headingRef.current, stepId !== 'verify');
      }}
      custom={direction}
      variants={{ exit: (currentDirection: 1 | -1) => ({ ...claimStepVariants(currentDirection, reduced).exit, transition: { duration: reduced ? 0 : .12, ease: claimMotion.ease.standard } }) }}
      exit="exit"
      transition={{ duration: reduced ? 0 : claimMotion.duration.step, ease: claimMotion.ease.standard }}
    >
        <h2 ref={headingRef} id={`cw-step-${stepId}`} tabIndex={-1} className="cw-step__title">{title}</h2>
        {hint ? <p className="cw-step__hint">{hint}</p> : null}
        {onSubmit ? (
          <form
            className="cw-step__form"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            {children}
            {/*
              * The default button, without which Enter does nothing.
              *
              * Implicit submission only fires when the form HAS a submit
              * button, unless it holds exactly one field that blocks it — and
              * this step has two. The visible Continue control is deliberately
              * `type="button"` (it also lives outside some steps' forms), so
              * without this the `<form>` wrapper would look like a fix and
              * change nothing.
              *
              * Hidden from sight and from the tab order, never `disabled`: a
              * disabled button is not a default button.
              */}
            <button
              type="submit"
              tabIndex={-1}
              aria-hidden="true"
              style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', border: 0 }}
            />
          </form>
        ) : (
          children
        )}
    </motion.section>
  );
}

export function ClaimStepActions({
  onBack,
  onNext,
  nextLabel = 'Continue',
  nextBusy = false,
  nextDisabled = false,
  backDisabled = false,
  children,
}: {
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextBusy?: boolean;
  nextDisabled?: boolean;
  backDisabled?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="cw-actions">
      {onBack ? (
        <button type="button" className="cw-actions__back" onClick={onBack} disabled={backDisabled}>
          Back
        </button>
      ) : <span />}
      {children}
      {onNext ? (
        <button
          type="button"
          className="cw-actions__next"
          onClick={onNext}
          disabled={nextDisabled || nextBusy}
          aria-busy={nextBusy || undefined}
        >
          {nextBusy ? 'Working…' : nextLabel}
        </button>
      ) : null}
    </div>
  );
}

/** Money for display only — the server priced everything that matters. */
export function formatClaimMoney(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
  } catch {
    // An unknown ISO code must not blank the price out entirely.
    return `${currency} ${value.toFixed(2)}`;
  }
}

/**
 * Which way the wizard just moved, so the panel can animate in from the side
 * the user came from.
 *
 * A ref, not state: this is derived from a render that has already been
 * committed, and setting state during render (which an earlier draft of this
 * did inside a useMemo) causes an extra render pass and a React warning. The
 * ref updates in an effect, after the direction has been used.
 */
export function useStepDirection(index: number): 1 | -1 {
  const previous = useRef(index);
  const direction: 1 | -1 = index >= previous.current ? 1 : -1;
  useEffect(() => {
    previous.current = index;
  }, [index]);
  return direction;
}
